import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// API functions for vendor service packages
export const vendorServicePackageApi = {
  // Get all packages for a vendor
  getVendorServicePackages: async (vendorId, params = {}) => {
    const response = await api.get(`/vendors/${vendorId}/service-packages`, { params });
    return response.data;
  },

  // Get current vendor's packages
  getMyServicePackages: async (params = {}) => {
    const response = await api.get('/vendor/service-packages', { params });
    return response.data;
  },

  // Get package by ID
  getById: async (packageId) => {
    const response = await api.get(`/vendor/service-packages/${packageId}`);
    return response.data;
  },

  // Create new package
  create: async (data) => {
    const response = await api.post('/vendor/service-packages', data);
    return response.data;
  },

  // Update package
  update: async ({ id, data }) => {
    const response = await api.put(`/vendor/service-packages/${id}`, data);
    return response.data;
  },

  // Delete package
  delete: async (id) => {
    const response = await api.delete(`/vendor/service-packages/${id}`);
    return response.data;
  },

  // Toggle package status (active/inactive)
  toggleStatus: async (id) => {
    const response = await api.put(`/vendor/service-packages/${id}/toggle-status`);
    return response.data;
  },

  // Toggle package popular status
  togglePopular: async (id, isAdmin = false) => {
    const endpoint = isAdmin 
      ? `/admin/vendor-service-packages/${id}/toggle-popular`
      : `/vendor/service-packages/${id}/toggle-popular`;
    const response = await api.put(endpoint);
    return response.data;
  }
};

// React Query hooks
export const useVendorServicePackages = (vendorId, params = {}) => {
  return useQuery({
    queryKey: ['vendor-service-packages', vendorId, params],
    queryFn: () => vendorServicePackageApi.getVendorServicePackages(vendorId, params),
    enabled: !!vendorId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useMyServicePackages = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['my-service-packages', params],
    queryFn: () => vendorServicePackageApi.getMyServicePackages(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useVendorServicePackage = (packageId) => {
  return useQuery({
    queryKey: ['vendor-service-package', packageId],
    queryFn: () => vendorServicePackageApi.getById(packageId),
    enabled: !!packageId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateVendorServicePackage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: vendorServicePackageApi.create,
    onSuccess: (data) => {
      // Invalidate and refetch packages
      queryClient.invalidateQueries({ queryKey: ['my-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-packages'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useUpdateVendorServicePackage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: vendorServicePackageApi.update,
    onSuccess: (data, variables) => {
      // Invalidate and refetch packages
      queryClient.invalidateQueries({ queryKey: ['my-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-package', variables.id] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useDeleteVendorServicePackage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: vendorServicePackageApi.delete,
    onSuccess: (data, packageId) => {
      // Invalidate and refetch packages
      queryClient.invalidateQueries({ queryKey: ['my-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-package', packageId] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useTogglePackageStatus = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: vendorServicePackageApi.toggleStatus,
    onSuccess: (data, packageId) => {
      // Invalidate and refetch packages
      queryClient.invalidateQueries({ queryKey: ['my-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-package', packageId] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useTogglePackagePopular = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params) => {
      // Handle both object { id, isAdmin } and simple id
      if (typeof params === 'object' && params.id) {
        return vendorServicePackageApi.togglePopular(params.id, params.isAdmin);
      }
      return vendorServicePackageApi.togglePopular(params, false);
    },
    onSuccess: (data, variables) => {
      // Extract packageId from variables (could be id or { id, isAdmin })
      const packageId = typeof variables === 'object' && variables.id ? variables.id : variables;
      
      // Invalidate and refetch packages
      queryClient.invalidateQueries({ queryKey: ['my-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-packages'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-service-package', packageId] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export default vendorServicePackageApi;
